import ExcelJS from 'exceljs';
import React from 'react';
import TGButton from 'src/components/ui/TGButton';
import { DownloadIcon } from 'src/components/ui-icons/upload';
import { monthsRegion } from '../reports/reportRegion/filterDataRegion';
import { TooltipItem } from 'src/utils/Tooltip';
import { filterDefault } from '../../types/typesVPO';

interface ExportToExcelProps {
  reportsData?: any;
  groupTitle: string;
  FilterColumns: filterDefault[];
  FactoryVPO: filterDefault[];
}

const ExportToExcelRegion: React.FC<ExportToExcelProps> = ({ reportsData, groupTitle, FilterColumns, FactoryVPO }) => {
  const getColorForValue = (value: number | null): string => {
    if (value === null) return 'FFE5E7EB';
    if (value >= 0 && value < 50) return 'FFFDD8D8';
    if (value >= 50 && value < 60) return 'FFFDD8D8';
    if (value >= 60 && value < 70) return 'FFFEF3C7';
    if (value >= 70) return 'FFC2F3D6';
    return 'FFE5E7EB';
  };

  const getPercentage = (factoryId: string | number, columnId: string | number, monthIndex: number): number | null => {
    if (!reportsData?.data?.[factoryId]) return null;
    const monthData = reportsData.data[factoryId][monthIndex + 1];
    if (!monthData?.directions?.[columnId]) return null;
    const directionData = monthData.directions[columnId];
    if (directionData.totalValid === 0) return null;
    return directionData.percentage;
  };

  const getAveragePercentage = (factoryId: string | number, monthIndex: number): number | null => {
    if (!reportsData?.data?.[factoryId]) return null;
    const monthData = reportsData.data[factoryId][monthIndex + 1];
    if (!monthData) return null;
    if (monthData.totalValid === 0) return null;
    return monthData.averagePercentage;
  };

  const getGroupAveragePercentage = (monthIndex: number): number | null => {
    if (!reportsData?.data || !FactoryVPO || FactoryVPO.length === 0) return null;
    let totalYes = 0;
    let totalValid = 0;
    FactoryVPO.forEach((factory) => {
      const factoryId = factory.ID;
      const monthData = reportsData.data[factoryId]?.[monthIndex + 1];
      if (monthData) {
        totalYes += monthData.totalYes || 0;
        totalValid += monthData.totalValid || 0;
      }
    });

    return totalValid > 0 ? Math.round((totalYes * 100) / totalValid) : null;
  };

  const getGroupColumnPercentage = (columnId: string | number, monthIndex: number): number | null => {
    if (!reportsData?.data || !FactoryVPO || FactoryVPO.length === 0) return null;
    let totalYes = 0;
    let totalValid = 0;
    FactoryVPO.forEach((factory) => {
      const factoryId = factory.ID;
      const monthData = reportsData.data[factoryId]?.[monthIndex + 1];
      if (monthData?.directions?.[columnId]) {
        totalYes += monthData.directions[columnId].yesCount || 0;
        totalValid += monthData.directions[columnId].totalValid || 0;
      }
    });
    return totalValid > 0 ? Math.round((totalYes * 100) / totalValid) : null;
  };

  const createTableInSheet = (sheet: ExcelJS.Worksheet, title: string, startRow: number, factoryId: string | number | null = null, isGroup: boolean = false) => {
    const titleRow = sheet.addRow([title]);
    titleRow.height = 30;
    titleRow.font = { size: 14, bold: true };
    sheet.mergeCells(`A${startRow}:${String.fromCharCode(64 + monthsRegion.length + 1)}${startRow}`);
    titleRow.alignment = { horizontal: 'center' };

    const headers = ['Колонна', ...monthsRegion];
    const headerRow = sheet.addRow(headers);
    headerRow.font = { size: 12, bold: true, color: { argb: 'FFFFFF' } };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4682B4' },
      };
      cell.alignment = { horizontal: 'center' };
    });

    FilterColumns.forEach((column) => {
      const rowData = [column.Code || column.Title];
      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const percentage = isGroup ? getGroupColumnPercentage(column.ID, monthIndex) : getPercentage(factoryId!, column.ID, monthIndex);
        rowData.push(percentage === null || percentage === undefined ? '-' : `${percentage}%`);
      }

      const dataRow = sheet.addRow(rowData);
      dataRow.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          const value = cell.value === '-' ? null : parseInt(String(cell.value).replace('%', ''));
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: getColorForValue(value) },
          };
          cell.alignment = { horizontal: 'center' };
        } else {
          cell.alignment = { horizontal: 'left' };
        }
        cell.font = { bold: true, color: { argb: 'FF000000' } };
      });
    });

    const averageRow = ['Всего средняя'];
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const totalAverage = isGroup ? getGroupAveragePercentage(monthIndex) : getAveragePercentage(factoryId!, monthIndex);
      averageRow.push(totalAverage === null || totalAverage === undefined ? '-' : `${totalAverage}%`);
    }

    const totalRow = sheet.addRow(averageRow);
    totalRow.eachCell((cell, colNumber) => {
      if (colNumber > 1) {
        const value = cell.value === '-' ? null : parseInt(String(cell.value).replace('%', ''));
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: getColorForValue(value) },
        };

        cell.alignment = { horizontal: 'center' };
      } else {
        cell.alignment = { horizontal: 'left' };
      }
      cell.font = { bold: true, color: { argb: 'FF000000' } };
    });

    sheet.addRow([]);

    const lastRow = sheet.lastRow!.number;
    for (let row = startRow; row <= lastRow - 1; row++) {
      sheet.getRow(row).eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    }

    return lastRow + 1;
  };

  const exportExcelFile = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Отчет по региону');

      sheet.columns = [{ width: 20 }, ...monthsRegion.map(() => ({ width: 12 }))];

      let currentRow = 1;

      if (groupTitle === 'SOP') {
        currentRow = createTableInSheet(sheet, 'ПЭТ Производство Казань', currentRow, null, true);
      } else {
        currentRow = createTableInSheet(sheet, groupTitle, currentRow, null, true);
        FactoryVPO.forEach((factory) => {
          currentRow = createTableInSheet(sheet, factory.Title, currentRow, factory.ID?.toString() || null, false);
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Отчет_по_региону_${groupTitle}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка при экспорте файла:', error);
    }
  };

  return (
    <TooltipItem position='left' tooltipsText={'Экспорт в Excel'}>
      <TGButton onClick={exportExcelFile} icon={<DownloadIcon />} iconStyle='w-5 h-5' disabled={!reportsData || !reportsData.data}>
        Скачать в Excel
      </TGButton>
    </TooltipItem>
  );
};

export default ExportToExcelRegion;
